import { RequestError, RequestFailureType } from '@amazon/vinyl-util'
import { isServerFaultGqlError, isUnauthorizedGqlError } from '@/service/gqlErrors'
import { UnauthorizedError } from '@amzn/bf-playback'

/**
 * The failure kind a route error maps to, so the router's error surface can
 * tailor its copy/CTAs — mirrors green hornet's ErrorSource split for the
 * unauthorized / network / server trio, with `unknown` as the generic default.
 */
export type RouteErrorKind = 'unauthorized' | 'network' | 'chunkLoad' | 'server' | 'unknown'

// Chromium, Firefox and WebKit's failed dynamic import, and Vite's failed CSS preload.
const CHUNK_LOAD_FAILURE =
    /dynamically imported module|Importing a module script failed|Unable to preload CSS/

/**
 * Classify a caught route error. A network-transport failure surfaces as a
 * `RequestError` (NETWORK) and a non-ok response as one carrying an HTTP
 * status; a Firefly auth/entitlement denial or 5xx surfaces as a `GqlError`
 * (its wire status dropped in the RequestError → GqlError conversion, so the
 * error code/message is the only signal left).
 */
export function classifyRouteError(error: unknown): RouteErrorKind {
    if (error instanceof UnauthorizedError) return 'unauthorized'
    if (error instanceof Error && CHUNK_LOAD_FAILURE.test(error.message)) return 'chunkLoad'
    if (error instanceof RequestError) {
        if (error.info.type === RequestFailureType.NETWORK) return 'network'
        if (error.info.type === RequestFailureType.RESPONSE) {
            const { status } = error.info.responseInfo
            if (status === 401 || status === 403) return 'unauthorized'
            if (status >= 500 && status < 600) return 'server'
        }
        return 'unknown'
    }
    if (isUnauthorizedGqlError(error)) return 'unauthorized'
    if (isServerFaultGqlError(error)) return 'server'
    return 'unknown'
}
